import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeftIcon,
  CpuChipIcon,
  PencilIcon,
  DocumentTextIcon,
  TrashIcon,
  PlusIcon,
  InformationCircleIcon,
  GlobeAltIcon,
  CloudArrowUpIcon,
  XMarkIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';
import toast from '../utils/toast';

const AgentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [documents, setDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [uploadingDocuments, setUploadingDocuments] = useState(false);
  
  // Website training states
  const [websiteTrainingEnabled, setWebsiteTrainingEnabled] = useState(false);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [websiteVerificationStatus, setWebsiteVerificationStatus] = useState('idle');
  const [websiteVerificationError, setWebsiteVerificationError] = useState('');
  
  // FAQ training states
  const [faqTrainingEnabled, setFaqTrainingEnabled] = useState(false);
  const [faqPairs, setFaqPairs] = useState([{ question: '', answer: '', id: Date.now() }]);

  // Document limits state
  const [documentLimits, setDocumentLimits] = useState({
    maxDocumentSizeBytes: 4194304, // 4MB default (scale package)
    maxTextLength: 4000, // 4K default (scale package)
    maxTokensPerDocument: 1000 // 1K default (scale package)
  });
  const [loadingLimits, setLoadingLimits] = useState(true);
  const [maxSystemPromptTokens, setMaxSystemPromptTokens] = useState(2000);

  // Document upload states
  const [dragActive, setDragActive] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  
  // FAQ helper functions
  const addFaqPair = () => {
    setFaqPairs(prev => [...prev, { question: '', answer: '', id: Date.now() }]);
  };

  const removeFaqPair = (id) => {
    setFaqPairs(prev => prev.filter(pair => pair.id !== id));
  };

  const updateFaqPair = (id, field, value) => {
    setFaqPairs(prev => prev.map(pair =>
      pair.id === id ? { ...pair, [field]: value } : pair
    ));
  };

  const fetchAgent = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.getAgent(id);
      const agentData = response.data.agent;
      setAgent(agentData);
      setFormData(agentData);

      // Auto-populate training options
      setWebsiteTrainingEnabled(!!agentData.website_url);
      setWebsiteUrl(agentData.website_url || '');

      // TODO: Fetch FAQ data if available
      // For now, initialize with default state
      setFaqTrainingEnabled(false); // This should be updated when backend supports FAQ data

    } catch (error) {
      console.error('Error fetching agent:', error);
      toast.error('Failed to load agent details');
      navigate('/dashboard/agents');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const fetchDocumentLimits = useCallback(async () => {
    try {
      // Get user's organization ID from localStorage
      const organizationId = localStorage.getItem('organization_id');
      if (organizationId) {
        // Call API to get package limits
        const response = await api.getPackageLimits(organizationId);
        console.log('Package limits API response:', response);

        if (response.data && response.data.limits) {
          setDocumentLimits({
            maxDocumentSizeBytes: response.data.limits.find(l => l.limit_type === 'max_document_size_bytes')?.limit_value || 4194304,
            maxTextLength: response.data.limits.find(l => l.limit_type === 'max_text_length')?.limit_value || 4000,
            maxTokensPerDocument: response.data.limits.find(l => l.limit_type === 'max_tokens_per_document')?.limit_value || 1000
          });
        } else {
          console.warn('No limits data in API response, using defaults');
          setDocumentLimits({
            maxDocumentSizeBytes: 4194304,
            maxTextLength: 4000,
            maxTokensPerDocument: 1000
          });
        }
      } else {
        console.warn('No organization ID found, using defaults');
        setDocumentLimits({
          maxDocumentSizeBytes: 4194304,
          maxTextLength: 4000,
          maxTokensPerDocument: 1000
        });
      }
    } catch (error) {
      console.error('Error fetching package limits:', error);
      setDocumentLimits({
        maxDocumentSizeBytes: 4194304,
        maxTextLength: 4000,
        maxTokensPerDocument: 1000
      });
    } finally {
      setLoadingLimits(false);
    }
  }, []);

  // Fetch system settings
  const fetchSystemSettings = async () => {
    try {
      const authToken = localStorage.getItem('snwolley_auth_token');
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/system/settings`, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.settings.max_system_prompt_tokens) {
          setMaxSystemPromptTokens(data.settings.max_system_prompt_tokens);
        }
      }
    } catch (error) {
      console.error('Failed to fetch system settings:', error);
    }
  };
  
  const populateExistingDocuments = useCallback(() => {
    // Convert existing documents to upload file format for editing
    const existingFiles = documents.map(doc => ({
      id: `existing_${doc.id}`,
      existingDocId: doc.id,
      name: doc.original_filename,
      size: doc.file_size || 0,
      status: 'existing',
      isExisting: true
    }));
    setUploadFiles(existingFiles);
  }, [documents]);

  const fetchDocuments = useCallback(async () => {
    try {
      setLoadingDocuments(true);
      const response = await api.getAgentDocuments(id);
      setDocuments(response.data.documents || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoadingDocuments(false);
    }
  }, [id]);
  
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : 
              type === 'number' ? parseFloat(value) || 0 : value
    }));
  };
  
  const handleWebsiteUrlChange = (e) => {
    const url = e.target.value;
    setWebsiteUrl(url);
    setFormData(prev => ({ ...prev, website_url: url }));
    
    // Reset verification status when URL changes
    if (url !== agent?.website_url) {
      setWebsiteVerificationStatus('idle');
      setWebsiteVerificationError('');
    }
  };
  
  const verifyWebsite = async () => {
    if (!websiteUrl.trim()) {
      toast.error('Please enter a website URL');
      return;
    }
    
    try {
      setWebsiteVerificationStatus('verifying');
      setWebsiteVerificationError('');
      
      await api.verifyWebsite(websiteUrl);
      setWebsiteVerificationStatus('success');
      toast.success('Website verified successfully!');
      
      // Update form data
      setFormData(prev => ({
        ...prev, 
        website_url: websiteUrl,
        website_verified: true
      }));
      
    } catch (error) {
      console.error('Website verification failed:', error);
      const errorMessage = error.response?.data?.error || 'Website verification failed';
      setWebsiteVerificationStatus('error');
      setWebsiteVerificationError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const handleSave = async () => {
    try {
      // Validate system prompt token limit (frontend check)
      const instructionsLength = formData.instructions?.trim().length || 0;
      const estimatedTokens = Math.ceil(instructionsLength / 4); // Rough estimation: 4 chars per token
      const maxTokens = maxSystemPromptTokens; // Use dynamic value from backend
      
      if (estimatedTokens > maxTokens) {
        toast.error(`System prompt too long (${estimatedTokens.toLocaleString()} tokens). Maximum allowed: ${maxTokens.toLocaleString()} tokens. Please reduce the length.`, {
          duration: 6000
        });
        return;
      }

      // Check if there are new files to upload
      const newFiles = uploadFiles.filter(file => !file.isExisting && file.file);
      
      console.log('HandleSave - uploadFiles:', uploadFiles);
      console.log('HandleSave - newFiles:', newFiles);
      
      if (newFiles.length > 0) {
        // Enhanced validation for file objects
        const validNewFiles = newFiles.filter(fileObj => {
          const isValid = fileObj.file && 
                         fileObj.file instanceof File && 
                         fileObj.file.size > 0 && 
                         fileObj.file.name && 
                         fileObj.file.name.trim() !== '';
          
          if (!isValid) {
            console.warn('Invalid file object:', fileObj);
          }
          
          return isValid;
        });
        
        console.log('HandleSave - validNewFiles:', validNewFiles);
        
        if (validNewFiles.length === 0) {
          toast.error('No valid files to upload. Please select valid files.');
          return;
        }
        
        if (validNewFiles.length !== newFiles.length) {
          toast.warning(`${newFiles.length - validNewFiles.length} invalid files were skipped`);
        }
        
        // Upload new documents first
        setUploadingDocuments(true);
        const formData = new FormData();
        
        // Enhanced file addition with validation
        validNewFiles.forEach((fileObj, index) => {
          console.log(`Adding file ${index + 1} to FormData:`, {
            name: fileObj.file.name,
            size: fileObj.file.size,
            type: fileObj.file.type,
            lastModified: fileObj.file.lastModified
          });
          formData.append('documents', fileObj.file);
        });
        
        // Enhanced debugging: Check FormData contents
        console.log('FormData has files:', formData.has('documents'));
        const formDataEntries = Array.from(formData.entries());
        console.log('FormData entries count:', formDataEntries.length);
        console.log('FormData entries:', formDataEntries.map(([key, value]) => ({
          key,
          fileName: value.name || 'no name',
          size: value.size || 'no size',
          type: value.type || 'no type'
        })));

        // Only proceed if we actually have files in FormData
        if (!formData.has('documents') || formDataEntries.length === 0) {
          toast.error('Failed to prepare files for upload. Please try again.');
          setUploadingDocuments(false);
          return;
        }

        try {
          console.log('About to upload to agent:', id);
          await api.uploadAgentDocuments(id, formData);
          toast.success(`${validNewFiles.length} new document(s) uploaded successfully`);
        } catch (uploadError) {
          console.error('Error uploading documents:', uploadError);
          console.error('Upload error response:', uploadError.response);

          // Handle backend validation errors with detailed messages
          if (uploadError.response?.status === 413) {
            // Show the general error first
            if (uploadError.response.data?.error) {
              toast.error(uploadError.response.data.error, {
                duration: 6000
              });
            }

            // Show the detailed message if available
            if (uploadError.response.data?.message) {
              toast.error(uploadError.response.data.message, {
                duration: 8000 // Longer duration for detailed error messages
              });
            }
          } else {
            // Fallback to generic error handling
            let uploadErrorMessage = 'Failed to upload documents';

            if (uploadError.response?.data?.error) {
              uploadErrorMessage = uploadError.response.data.error;
            } else if (uploadError.message) {
              uploadErrorMessage = uploadError.message;
            }

            // Add helpful context for common errors
            if (uploadErrorMessage.includes('No files uploaded')) {
              uploadErrorMessage += '. Please ensure your files are valid and try again.';
            }

            toast.error(uploadErrorMessage);
          }

          setUploadingDocuments(false);
          return; // Stop the save process if document upload fails
        }
        setUploadingDocuments(false);
      }
      
      // Include website and training data in the save
      const updateData = {
        ...formData,
        website_url: websiteUrl
      };
      
      const response = await api.updateAgent(id, updateData);
      setAgent(response.data.agent);
      setIsEditing(false);
      
      // Clear upload files and refresh documents
      setUploadFiles([]);
      fetchDocuments();
      
      if (newFiles.length > 0) {
        toast.success('Agent updated successfully with new documents uploaded');
      } else {
        toast.success('Agent updated successfully');
      }
      
      // TODO: Save FAQ data when backend supports it
      
    } catch (error) {
      console.error('Error updating agent:', error);
      const errorMessage = error.response?.data?.error || 'Failed to update agent';
      toast.error(errorMessage);
    } finally {
      setUploadingDocuments(false);
    }
  };
  
  // File upload handlers
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    handleFiles(files);
  };

  const handleFiles = (files) => {
    console.log('HandleFiles called with:', files);
    
    const validFiles = files.filter(file => {
      const validTypes = ['application/pdf', 'text/plain', 'application/msword',
                          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                          'text/csv', 'application/json'];

      const isValidType = validTypes.includes(file.type) || file.name.match(/\.(pdf|txt|doc|docx|csv|json|md)$/i);
      const isValidSize = file.size > 0 && file.size <= documentLimits.maxDocumentSizeBytes;
      const isValidName = file.name && file.name.trim() !== '';

      console.log(`File validation - ${file.name}:`, {
        type: file.type,
        size: file.size,
        maxSize: documentLimits.maxDocumentSizeBytes,
        isValidType,
        isValidSize,
        isValidName,
        overall: isValidType && isValidSize && isValidName
      });

      return isValidType && isValidSize && isValidName;
    });

    console.log('Valid files after filtering:', validFiles);

    if (validFiles.length !== files.length) {
      const rejectedCount = files.length - validFiles.length;
      toast.error(`${rejectedCount} file(s) were rejected. Please upload PDF, TXT, DOC, DOCX, CSV, JSON, or MD files only.`);
    }

    const newFiles = validFiles.map(file => {
      const fileObj = {
        id: Date.now() + Math.random(),
        file,
        name: file.name,
        size: file.size,
        type: file.type,
        status: 'ready',
        isExisting: false
      };
      
      console.log('Created file object:', fileObj);
      return fileObj;
    });

    console.log('Setting uploadFiles with new files:', newFiles);
    setUploadFiles(prev => {
      const combined = [...prev, ...newFiles];
      console.log('Combined uploadFiles:', combined);
      return combined;
    });
  };

  const removeUploadFile = async (fileId) => {
    const fileToRemove = uploadFiles.find(file => file.id === fileId);
    
    if (fileToRemove && fileToRemove.isExisting) {
      // This is an existing document - delete it from the server
      if (window.confirm(`Are you sure you want to delete "${fileToRemove.name}"? This action cannot be undone.`)) {
        try {
          await handleDeleteDocument(fileToRemove.existingDocId, fileToRemove.name);
          // Remove from upload files list
          setUploadFiles(prev => prev.filter(file => file.id !== fileId));
        } catch (error) {
          // Error already handled in handleDeleteDocument
        }
      }
    } else {
      // This is a new file - just remove from the list
      setUploadFiles(prev => prev.filter(file => file.id !== fileId));
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete agent "${agent.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await api.deleteAgent(id);
      toast.success('Agent deleted successfully');
      navigate('/dashboard/agents');
    } catch (error) {
      console.error('Error deleting agent:', error);
      const errorMessage = error.response?.data?.error || 'Failed to delete agent';
      toast.error(errorMessage);
    }
  };

  const handleDocumentUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = '.pdf,.doc,.docx,.txt,.md';
    input.onchange = async (e) => {
      const files = Array.from(e.target.files);
      if (files.length === 0) return;

      try {
        setUploadingDocuments(true);
        const formData = new FormData();
        files.forEach(file => {
          formData.append('documents', file);
        });

        await api.uploadAgentDocuments(id, formData);
        toast.success(`${files.length} document(s) uploaded successfully`);
        fetchDocuments(); // Refresh documents list
      } catch (error) {
        console.error('Error uploading documents:', error);

        // Handle backend validation errors with detailed messages
        if (error.response?.status === 413) {
          // Show the general error first
          if (error.response.data?.error) {
            toast.error(error.response.data.error, {
              duration: 6000
            });
          }

          // Show the detailed message if available
          if (error.response.data?.message) {
            toast.error(error.response.data.message, {
              duration: 8000 // Longer duration for detailed error messages
            });
          }
        } else {
          // Fallback to generic error handling
          const errorMessage = error.response?.data?.error || 'Failed to upload documents';
          toast.error(errorMessage);
        }
      } finally {
        setUploadingDocuments(false);
      }
    };
    input.click();
  };

  const viewKnowledgeBaseDocument = (documentId, filename) => {
    const authToken = localStorage.getItem('snwolley_auth_token');
    const url = `${process.env.REACT_APP_API_BASE_URL}/api/agents/${id}/documents/${documentId}/download?token=${authToken}`;
    window.open(url, '_blank');
  };

  const downloadKnowledgeBaseDocument = (documentId, filename) => {
    const authToken = localStorage.getItem('snwolley_auth_token');
    const url = `${process.env.REACT_APP_API_BASE_URL}/api/agents/${id}/documents/${documentId}/download?token=${authToken}`;
    
    // Create a temporary link to trigger download
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success(`Downloading ${filename}...`);
  };

  const handleDeleteDocument = async (documentId, filename) => {
    if (!window.confirm(`Are you sure you want to delete "${filename}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await api.deleteAgentDocument(id, documentId);
      toast.success('Document deleted successfully');
      
      // Clear cache for this agent since knowledge base changed
      try {
        // Call the cache invalidation endpoint
        const invalidateResponse = await fetch(`/cache/invalidate/${id}`, {
          method: 'POST',
          headers: {
            'X-API-Key': 'AlphanumericAndSpace', // TODO: Use dynamic API key
            'Content-Type': 'application/json'
          }
        });
        
        if (invalidateResponse.ok) {
          console.log('Agent cache invalidated successfully');
        } else {
          console.warn('Cache invalidation failed, but document was deleted');
        }
      } catch (cacheError) {
        console.warn('Cache invalidation request failed:', cacheError);
        // Don't fail the operation if cache invalidation fails
      }
      
      fetchDocuments(); // Refresh documents list
    } catch (error) {
      console.error('Error deleting document:', error);
      const errorMessage = error.response?.data?.error || 'Failed to delete document';
      toast.error(errorMessage);
    }
  };

  useEffect(() => {
    fetchAgent();
    fetchDocuments();
    fetchDocumentLimits();
    fetchSystemSettings();
  }, [id, fetchAgent, fetchDocuments, fetchDocumentLimits]);
  
  // Populate existing documents when entering edit mode
  useEffect(() => {
    if (isEditing && documents.length > 0 && formData.knowledge_base_enabled) {
      populateExistingDocuments();
    } else if (!isEditing) {
      // Clear upload files when exiting edit mode
      setUploadFiles([]);
    }
  }, [isEditing, documents, formData.knowledge_base_enabled, populateExistingDocuments]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-800',
      training: 'bg-yellow-100 text-yellow-800'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusClasses[status] || 'bg-gray-100 text-gray-800'}`}>
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </span>
    );
  };



  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900">Agent not found</h3>
        <button
          onClick={() => navigate('/dashboard/agents')}
          className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
        >
          Back to Agents
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
                  <button
          onClick={() => navigate('/dashboard/agents')}
          className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
        >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Back to Agents
          </button>
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
              <CpuChipIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{agent.name}</h1>
              <div className="flex items-center space-x-3 mt-1">
                {getStatusBadge(agent.status)}
                <span className="text-sm text-gray-500">
                  Created {formatDate(agent.created_at)}
                </span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          {isEditing ? (
            <>
              <button
                onClick={() => {
                  setIsEditing(false);
                  setFormData(agent);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={uploadingDocuments}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploadingDocuments ? 'Uploading Documents...' : 'Save Changes'}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setIsEditing(true)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <PencilIcon className="h-4 w-4 mr-2" />
                Edit
              </button>
              <button
                onClick={handleDelete}
                className="inline-flex items-center px-4 py-2 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50"
              >
                <TrashIcon className="h-4 w-4 mr-2" />
                Delete
              </button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information */}
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Basic Information</h3>
              <p className="text-sm text-gray-500 mt-1">
                Configure the basic details of your AI agent
              </p>
            </div>
            <div className="px-6 py-4 space-y-4">
              {isEditing ? (
                <>
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                      Agent Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      id="name"
                      required
                      value={formData.name || ''}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="e.g., Customer Support Assistant"
                    />
                  </div>

                  <div>
                    <label htmlFor="instructions" className="block text-sm font-medium text-gray-700">
                      System Instructions *
                    </label>
                    <div className="mt-1">
                      <textarea
                        name="instructions"
                        id="instructions"
                        rows={6}
                        required
                        value={formData.instructions || ''}
                        onChange={handleInputChange}
                        className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        placeholder="Provide detailed instructions for how this agent should behave and respond..."
                      />
                    </div>
                    {/* Token Counter */}
                    <div className="mt-2 flex justify-between items-center">
                      <p className="text-sm text-gray-500">
                        These instructions define how your AI agent will behave and respond to messages.
                      </p>
                      <div className="text-sm">
                        {(() => {
                          const charCount = formData.instructions?.length || 0;
                          const estimatedTokens = Math.ceil(charCount / 4);
                          const maxTokens = maxSystemPromptTokens;
                          const isNearLimit = estimatedTokens > maxTokens * 0.8;
                          const isOverLimit = estimatedTokens > maxTokens;
                          
                          return (
                            <span className={`font-medium ${
                              isOverLimit ? 'text-red-600' :
                              isNearLimit ? 'text-amber-600' :
                              'text-gray-500'
                            }`}>
                              ~{estimatedTokens.toLocaleString()} / {maxTokens.toLocaleString()} tokens
                            </span>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/*<div>*/}
                  {/*  <dt className="text-sm font-medium text-gray-500">Description</dt>*/}
                  {/*  <dd className="mt-1 text-sm text-gray-900">{agent.description || 'No description provided'}</dd>*/}
                  {/*</div>*/}
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Instructions</dt>
                    <dd className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">
                      {agent.instructions}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Created by</dt>
                    <dd className="mt-1 text-sm text-gray-900">{agent.created_by_name}</dd>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Behavior Settings */}
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Behavior Settings</h3>
              <p className="text-sm text-gray-500 mt-1">
                Configure how the agent behaves in conversations
              </p>
            </div>
            <div className="px-6 py-4 space-y-4">
              {isEditing ? (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="response_delay_ms" className="block text-sm font-medium text-gray-700">
                        Response Delay (ms)
                      </label>
                      <input
                        type="number"
                        name="response_delay_ms"
                        id="response_delay_ms"
                        min="0"
                        max="10000"
                        step="100"
                        value={formData.response_delay_ms || 1000}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Base delay before responding (plus response token calculation time)
                      </p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center">
                      <input
                        id="auto_reply_enabled"
                        name="auto_reply_enabled"
                        type="checkbox"
                        checked={formData.auto_reply_enabled || false}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="auto_reply_enabled" className="ml-3 block text-sm font-medium text-gray-700">
                        Enable auto-reply
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        id="typing_simulation"
                        name="typing_simulation"
                        type="checkbox"
                        checked={formData.typing_simulation || false}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="typing_simulation" className="ml-3 block text-sm font-medium text-gray-700">
                        Simulate typing indicator
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        id="knowledge_base_enabled"
                        name="knowledge_base_enabled"
                        type="checkbox"
                        checked={formData.knowledge_base_enabled || false}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="knowledge_base_enabled" className="ml-3 block text-sm font-medium text-gray-700">
                        Enable knowledge base
                      </label>
                      <div className="ml-2 group relative">
                        <InformationCircleIcon className="h-4 w-4 text-gray-400" />
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                          Upload documents to train your agent with specific knowledge
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center">
                      <input
                        id="website_training_enabled"
                        name="website_training_enabled"
                        type="checkbox"
                        checked={websiteTrainingEnabled}
                        onChange={(e) => setWebsiteTrainingEnabled(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="website_training_enabled" className="ml-3 block text-sm font-medium text-gray-700">
                        Train with website
                      </label>
                      <div className="ml-2 group relative">
                        <InformationCircleIcon className="h-4 w-4 text-gray-400" />
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                          Train your agent with content from your website
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center">
                      <input
                        id="faq_training_enabled"
                        name="faq_training_enabled"
                        type="checkbox"
                        checked={faqTrainingEnabled}
                        onChange={(e) => setFaqTrainingEnabled(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="faq_training_enabled" className="ml-3 block text-sm font-medium text-gray-700">
                        Train with FAQ
                      </label>
                      <div className="ml-2 group relative">
                        <InformationCircleIcon className="h-4 w-4 text-gray-400" />
                        <div className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                          Add custom question-answer pairs to train your agent
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Response Delay</dt>
                    <dd className="mt-1 text-sm text-gray-900">{agent.response_delay_ms || 1000}ms</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Settings</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      <div className="space-y-1">
                        <div>Auto-reply: {agent.auto_reply_enabled ? 'Enabled' : 'Disabled'}</div>
                        <div>Typing simulation: {agent.typing_simulation ? 'Enabled' : 'Disabled'}</div>
                        <div>Knowledge base: {agent.knowledge_base_enabled ? 'Enabled' : 'Disabled'}</div>
                        <div>Website training: {agent.website_url ? 'Enabled' : 'Disabled'}</div>
                      </div>
                    </dd>
                  </div>
                </>
              )}
            </div>
          </div>
          
          {/* Training Options */}
          {isEditing && (
            <>
              {/* Knowledge Base Documents */}
              {formData.knowledge_base_enabled && (
                <div className="bg-white shadow sm:rounded-lg">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900">Knowledge Base Documents</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Upload documents to train your agent with specific knowledge (PDF, TXT, DOC, DOCX, CSV, JSON)
                    </p>
                  </div>
                  <div className="px-6 py-4 space-y-4">
                    {/* File Upload Dropzone */}
                    <div
                      className={`relative border-2 border-dashed rounded-lg p-6 transition-colors ${
                        dragActive
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                      onDragEnter={handleDrag}
                      onDragLeave={handleDrag}
                      onDragOver={handleDrag}
                      onDrop={handleDrop}
                    >
                      <div className="text-center">
                        <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                        <div className="mt-4">
                          <label htmlFor="file-upload" className="cursor-pointer">
                            <span className="mt-2 block text-sm font-medium text-gray-900">
                              Drop files here or click to upload
                            </span>
                            <input
                              id="file-upload"
                              type="file"
                              className="sr-only"
                              multiple
                              accept=".pdf,.doc,.docx,.txt,.csv,.json,.md"
                              onChange={handleFileSelect}
                            />
                          </label>
                          <p className="mt-1 text-xs text-gray-500">
                            PDF, TXT, DOC, DOCX, CSV, JSON, MD up to {Math.floor(documentLimits.maxDocumentSizeBytes / (1024 * 1024))}MB each
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Selected Files for Upload */}
                    {uploadFiles.length > 0 && (
                      <div className="space-y-2">
                        <h4 className="text-sm font-medium text-gray-700">
                          Documents ({uploadFiles.length} total):
                        </h4>
                        <div className="space-y-2">
                          {uploadFiles.map((file) => (
                            <div key={file.id} className={`flex items-center justify-between p-3 rounded-lg ${
                              file.isExisting ? 'bg-blue-50 border border-blue-200' : 'bg-green-50 border border-green-200'
                            }`}>
                              <div className="flex items-center space-x-3">
                                <DocumentTextIcon className={`h-5 w-5 ${
                                  file.isExisting ? 'text-blue-500' : 'text-green-500'
                                }`} />
                                <div>
                                  <div className="flex items-center space-x-2">
                                    <p className="text-sm font-medium text-gray-900">{file.name}</p>
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                      file.isExisting 
                                        ? 'bg-blue-100 text-blue-800' 
                                        : 'bg-green-100 text-green-800'
                                    }`}>
                                      {file.isExisting ? 'Existing' : 'New'}
                                    </span>
                                  </div>
                                  <p className="text-xs text-gray-500">
                                    {(file.size / 1024).toFixed(1)} KB
                                  </p>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeUploadFile(file.id)}
                                className="text-gray-400 hover:text-red-500"
                                title={file.isExisting ? 'Delete document' : 'Remove from upload'}
                              >
                                <XMarkIcon className="h-4 w-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                        
                        {/* Info about saving */}
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                          <div className="flex">
                            <InformationCircleIcon className="h-4 w-4 text-amber-400 mt-0.5" />
                            <div className="ml-2">
                              <p className="text-xs text-amber-700">
                                <strong>Blue badges:</strong> Existing documents that will be kept when you save. 
                                <strong>Green badges:</strong> New files to be uploaded when you click "Save Changes".
                                {uploadFiles.some(file => !file.isExisting) && (
                                  <span className="block mt-1 font-medium">
                                    {uploadFiles.filter(file => !file.isExisting).length} new document(s) ready to upload.
                                  </span>
                                )}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Knowledge Base Info */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex">
                        <InformationCircleIcon className="h-5 w-5 text-blue-400" />
                        <div className="ml-3">
                          <h4 className="text-sm font-medium text-blue-800">Knowledge Base Training</h4>
                          <p className="mt-1 text-sm text-blue-700">
                            Documents will be processed and used to train your agent. The agent will be able to answer questions based on the content of these documents.
                          </p>
                          <div className="mt-2">
                            <h5 className="text-xs font-medium text-blue-800">Supported Formats:</h5>
                            <ul className="text-xs text-blue-700 mt-1 list-disc list-inside">
                              <li>PDF files (.pdf)</li>
                              <li>Word documents (.doc, .docx)</li>
                              <li>Plain text (.txt)</li>
                              <li>CSV files (.csv)</li>
                              <li>JSON files (.json)</li>
                              <li>Markdown files (.md)</li>
                            </ul>
                            <p className="text-xs text-blue-600 mt-1">
                              Maximum: {Math.floor(documentLimits.maxDocumentSizeBytes / (1024 * 1024))}MB per file, {documentLimits.maxTextLength.toLocaleString()} characters, {documentLimits.maxTokensPerDocument.toLocaleString()} tokens
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Website Training */}
              {websiteTrainingEnabled && (
                <div className="bg-white shadow sm:rounded-lg">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900">Website Training</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Enter a website URL to train your agent with content from that website
                    </p>
                  </div>
                  <div className="px-6 py-4 space-y-4">
                    {/* Website URL Input */}
                    <div>
                      <label htmlFor="website_url" className="block text-sm font-medium text-gray-700 mb-2">
                        Website URL
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <GlobeAltIcon className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          type="text"
                          id="website_url"
                          name="website_url"
                          value={websiteUrl}
                          onChange={handleWebsiteUrlChange}
                          placeholder="https://example.com"
                          className="block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        />
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                          <button
                            type="button"
                            onClick={verifyWebsite}
                            disabled={websiteVerificationStatus === 'verifying' || !websiteUrl.trim()}
                            className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {websiteVerificationStatus === 'verifying' ? 'Verifying...' : 'Verify'}
                          </button>
                        </div>
                      </div>
                      {/* Error Message */}
                      {websiteVerificationStatus === 'error' && websiteVerificationError && (
                        <p className="mt-1 text-sm text-red-600">{websiteVerificationError}</p>
                      )}
                      {/* Success Message */}
                      {websiteVerificationStatus === 'success' && (
                        <p className="mt-1 text-sm text-green-600">Website verified successfully!</p>
                      )}
                    </div>

                    {/* Website Training Info */}
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex">
                        <InformationCircleIcon className="h-5 w-5 text-green-400" />
                        <div className="ml-3">
                          <h4 className="text-sm font-medium text-green-800">Website Content Training</h4>
                          <p className="mt-1 text-sm text-green-700">
                            Your agent will be trained with the content from the provided website. This allows the agent to answer questions based on the website's information.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* FAQ Training */}
              {faqTrainingEnabled && (
                <div className="bg-white shadow sm:rounded-lg">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900">FAQ Training</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Add question-answer pairs to train your agent with specific responses
                    </p>
                  </div>
                  <div className="px-6 py-4 space-y-4">
                    {/* FAQ Pairs */}
                    <div className="space-y-4">
                      {faqPairs.map((pair, index) => (
                        <div key={pair.id} className="border border-gray-200 rounded-lg p-4 space-y-4">
                          <div className="flex justify-between items-center">
                            <h4 className="text-sm font-medium text-gray-700">FAQ Pair {index + 1}</h4>
                            {faqPairs.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeFaqPair(pair.id)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <XMarkIcon className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                          <div className="grid grid-cols-1 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Question
                              </label>
                              <input
                                type="text"
                                value={pair.question}
                                onChange={(e) => updateFaqPair(pair.id, 'question', e.target.value)}
                                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                placeholder="e.g., What are your business hours?"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Answer
                              </label>
                              <textarea
                                rows={3}
                                value={pair.answer}
                                onChange={(e) => updateFaqPair(pair.id, 'answer', e.target.value)}
                                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                placeholder="e.g., We are open Monday to Friday from 9 AM to 6 PM."
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Add FAQ Pair Button */}
                    <button
                      type="button"
                      onClick={addFaqPair}
                      className="w-full border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <div className="text-gray-600">
                        <DocumentTextIcon className="mx-auto h-8 w-8 mb-2" />
                        <span className="block text-sm font-medium">Add Another FAQ Pair</span>
                        <span className="block text-xs text-gray-500 mt-1">Click to add more questions and answers</span>
                      </div>
                    </button>

                    {/* FAQ Training Info */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex">
                        <InformationCircleIcon className="h-5 w-5 text-blue-400" />
                        <div className="ml-3">
                          <h4 className="text-sm font-medium text-blue-800">FAQ Training</h4>
                          <p className="mt-1 text-sm text-blue-700">
                            Your agent will be trained with these question-answer pairs. When users ask similar questions, the agent will provide responses based on this training data.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Statistics */}
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Statistics</h3>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">Conversations</dt>
                <dd className="mt-1 text-2xl font-semibold text-gray-900">
                  {agent.total_conversations || 0}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Messages Processed</dt>
                <dd className="mt-1 text-2xl font-semibold text-gray-900">
                  {agent.total_messages_processed || 0}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Avg Response Time</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {agent.average_response_time_ms ? `${agent.average_response_time_ms}ms` : 'N/A'}
                </dd>
              </div>
            </div>
          </div>

          {/* Knowledge Base */}
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Knowledge Base</h3>
              {agent.knowledge_base_enabled && (
                <button 
                  onClick={handleDocumentUpload}
                  disabled={uploadingDocuments}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <PlusIcon className="h-4 w-4 mr-1" />
                  {uploadingDocuments ? 'Uploading...' : 'Add Document'}
                </button>
              )}
            </div>
            <div className="px-6 py-4">
              {agent.knowledge_base_enabled ? (
                <div>
                  {loadingDocuments ? (
                    <div className="text-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto"></div>
                      <p className="mt-2 text-sm text-gray-500">Loading documents...</p>
                    </div>
                  ) : documents && documents.length > 0 ? (
                    <div className="space-y-3">
                      {documents.map((doc) => (
                        <div key={doc.id} className="p-3 border border-gray-200 rounded-lg">
                          <div className="flex items-center space-x-3 mb-2">
                            <DocumentTextIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate" title={doc.original_filename}>
                                {doc.original_filename}
                              </p>
                              <p className="text-xs text-gray-500">
                                {doc.file_size ? `${(doc.file_size / 1024).toFixed(1)} KB` : 'Unknown size'} • {doc.processing_status || 'pending'}
                              </p>
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => viewKnowledgeBaseDocument(doc.id, doc.original_filename)}
                              className="flex-1 inline-flex items-center justify-center px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded hover:bg-blue-200 transition-colors"
                              title="View document"
                            >
                              <EyeIcon className="h-3 w-3 mr-1" />
                              View
                            </button>
                            <button
                              onClick={() => downloadKnowledgeBaseDocument(doc.id, doc.original_filename)}
                              className="flex-1 inline-flex items-center justify-center px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded hover:bg-green-200 transition-colors"
                              title="Download document"
                            >
                              <svg className="h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              Download
                            </button>
                            <button
                              onClick={() => handleDeleteDocument(doc.id, doc.original_filename)}
                              className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                              title="Delete document"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <DocumentTextIcon className="mx-auto h-8 w-8 text-gray-400" />
                      <p className="mt-2 text-sm text-gray-500">No documents uploaded</p>
                      <p className="text-xs text-gray-400">Click "Add Document" to upload knowledge base files</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-4">
                  <InformationCircleIcon className="mx-auto h-8 w-8 text-gray-400" />
                  <p className="mt-2 text-sm text-gray-500">Knowledge base is disabled</p>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default AgentDetail;
